// src/lib/resources/data/guides/images.ts

/**
 * @fileoverview Centralized image paths for resource center guides.
 * @description SEO-friendly filenames with high-res OG artwork (2400×1260,
 * `_AR-1.91-1_2400x1260_2x` convention) for social sharing, cards, and hero display.
 * @module resources/data/guides/images
 */

/** Fallback when a guide has no dedicated artwork. */
export const GUIDES_HUB_HERO =
  '/images/resources/guides/adsights-digital-marketing-resource-center-guides-tutorials-hero-image.webp'

/** Per-guide OG / hero image paths keyed by slug. */
export const GUIDE_IMAGES = {
  'how-to-spot-a-fake-screenshot': {
    hero: '/images/resources/guides/how-to-spot-fake-screenshot/adsights-how-to-spot-fake-screenshot-guide-og-image_AR-1.91-1_2400x1260_2x.jpg',
    width: 2400,
    height: 1260,
    alt: 'How to Spot a Fake Screenshot — visual red flags, metadata forensics, and verification workflow infographic',
  },
  'creative-testing-framework-guide': {
    hero: '/images/resources/guides/creative-testing-framework/adsights-creative-testing-framework-guide-og-image_AR-1.91-1_2400x1260_2x.jpg',
    width: 2400,
    height: 1260,
    alt: 'Creative Testing Framework — hypothesize, design, run, and interpret rigorous ad tests',
  },
  'thruplay-rate-interpretation-guide': {
    hero: '/images/resources/guides/thruplay-rate-interpretation/adsights-thruplay-rate-interpretation-guide-og-image_AR-1.91-1_2400x1260_2x.jpg',
    width: 2400,
    height: 1260,
    alt: 'ThruPlay Rate Interpretation Guide — benchmarks, metric definitions, and diagnostic workflow',
  },
  'video-ad-hook-optimization-guide': {
    hero: '/images/resources/guides/video-ad-hook-optimization/adsights-video-ad-hook-optimization-guide-og-image_AR-1.91-1_2400x1260_2x.jpg',
    width: 2400,
    height: 1260,
    alt: 'Video Ad Hook Optimization — pattern interrupt, thumbstop tactics, and hook testing framework',
  },
  'meta-ad-creative-video-specs-guide': {
    hero: '/images/resources/guides/meta-creative-specs/adsights-meta-video-ad-creative-specs-guide-og-image_AR-1.91-1_2400x1260_2x.jpg',
    width: 2400,
    height: 1260,
    alt: 'Meta Video Ad Creative Specs Guide — Feed, Stories, Reels, and in-stream placement specifications',
  },
  'video-creative-metrics-benchmarks-2026-guide': {
    hero: '/images/resources/guides/video-creative-metrics-benchmarks-2026/adsights-video-creative-metrics-benchmarks-2026-guide-og-image_AR-16x9_2400x1350_2x.jpg',
    width: 2400,
    height: 1350,
    alt: 'Video Creative Metrics Benchmarks 2026 — thumbstop, hold rate, and ThruPlay shown as three thresholds with their diagnostic questions',
  },
  'incrementality-testing-guide': {
    hero: '/images/resources/guides/incrementality-testing/adsights-incrementality-testing-guide-og-image_AR-16x9_2400x1350_2x.jpg',
    width: 2400,
    height: 1350,
    alt: 'Incrementality Testing Guide — Conversion Lift, geo holdout, and ghost-ads test designs with the iROAS decision framework',
  },
  'marketing-data-visualization-guide': {
    hero: '/images/resources/guides/marketing-data-visualization/adsights-marketing-data-visualization-guide-og-image_AR-16x9_2400x1350_2x.jpg',
    width: 2400,
    height: 1350,
    alt: 'Marketing Data Visualization Guide — which chart type to use for ROAS trends, funnels, distributions, and creative metrics',
  },
  'tiktok-ad-creative-video-specs-guide': {
    hero: '/images/resources/guides/tiktok-creative-specs/adsights-tiktok-video-ad-creative-specs-guide-og-image_AR-16x9_2400x1350_2x.jpg',
    width: 2400,
    height: 1350,
    alt: 'TikTok Video Ad Creative Specs Guide — In-Feed, Spark Ads, TopView, safe zones, and file requirements',
  },
} as const

/**
 * Build a ResourceImages block for a guide slug.
 *
 * @param {keyof typeof GUIDE_IMAGES} slug - Guide URL slug.
 * @returns {Object} Hero, thumbnail, and OG image references.
 */
export function getGuideImages(slug: keyof typeof GUIDE_IMAGES): {
  hero: { url: string; width: number; height: number; alt: string }
  thumbnail: { url: string; width: number; height: number; alt: string }
  og: { url: string; width: number; height: number; alt: string }
} {
  const config = GUIDE_IMAGES[slug]
  const image = { url: config.hero, width: config.width, height: config.height, alt: config.alt }
  return { hero: image, thumbnail: image, og: image }
}
